// UltraFlex Admin — Group Overview (Multi-Entity Consolidated View)
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Globe, Users, TrendingUp, DoorOpen, Building2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AdminGroupOverview() {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [sleeperStats, setSleeperStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [overRes, sleepRes] = await Promise.all([
        fetch(`${API}/api/operations/group-overview`, { headers }),
        fetch(`${API}/api/operations/sleeper-stats`, { headers }),
      ]);
      setData(await overRes.json());
      setSleeperStats(await sleepRes.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  return (
    <div className="space-y-6" data-testid="group-overview-page">
      <div>
        <h1 className="text-2xl font-bold text-white">Group Overview</h1>
        <p className="text-sm text-gray-400 mt-1">Consolidated view across all 11 entities</p>
      </div>

      {loading ? <div className="text-gray-500 text-center py-8">Loading...</div> : (
        <>
          {/* Group totals */}
          {data && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div className="bg-[#111] border border-gray-800 rounded-xl p-5 text-center">
                <Users className="w-6 h-6 mx-auto text-blue-400 mb-2" />
                <div className="text-3xl font-bold text-white">{data.totals?.members}</div>
                <div className="text-xs text-gray-500 uppercase mt-1">Total Members</div>
              </div>
              <div className="bg-[#111] border border-gray-800 rounded-xl p-5 text-center">
                <TrendingUp className="w-6 h-6 mx-auto text-green-400 mb-2" />
                <div className="text-3xl font-bold text-green-400">£{data.totals?.revenue?.toFixed(2)}</div>
                <div className="text-xs text-gray-500 uppercase mt-1">Monthly Revenue</div>
              </div>
              <div className="bg-[#111] border border-gray-800 rounded-xl p-5 text-center">
                <DoorOpen className="w-6 h-6 mx-auto text-purple-400 mb-2" />
                <div className="text-3xl font-bold text-purple-400">{data.totals?.access}</div>
                <div className="text-xs text-gray-500 uppercase mt-1">Access This Month</div>
              </div>
              <div className="bg-[#111] border border-gray-800 rounded-xl p-5 text-center">
                <Building2 className="w-6 h-6 mx-auto text-yellow-400 mb-2" />
                <div className="text-3xl font-bold text-yellow-400">{data.sites?.length}</div>
                <div className="text-xs text-gray-500 uppercase mt-1">Active Sites</div>
              </div>
            </div>
          )}

          {/* Sleeper member stats */}
          {sleeperStats && (
            <div className="bg-[#111] border border-gray-800 rounded-xl p-5">
              <h2 className="text-sm font-medium text-gray-400 uppercase mb-3">Sleeper Member Risk</h2>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div className="text-center">
                  <div className="text-2xl font-bold text-white">{sleeperStats.total_active_members}</div>
                  <div className="text-xs text-gray-500">Active Members</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-yellow-400">{sleeperStats.sleepers_30_days}</div>
                  <div className="text-xs text-gray-500">30+ Days Absent</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-orange-400">{sleeperStats.sleepers_60_days}</div>
                  <div className="text-xs text-gray-500">60+ Days Absent</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-red-400">{sleeperStats.sleepers_90_days}</div>
                  <div className="text-xs text-gray-500">90+ Days Absent</div>
                </div>
              </div>
            </div>
          )}

          {/* Per-site breakdown */}
          {data && (
            <div className="bg-[#111] border border-gray-800 rounded-xl overflow-hidden">
              <div className="p-3 border-b border-gray-800"><h2 className="text-sm font-medium text-gray-400 uppercase">Site Breakdown</h2></div>
              <table className="w-full text-sm">
                <thead><tr className="text-gray-500 text-xs uppercase border-b border-gray-800">
                  <th className="text-left p-3">Site</th><th className="text-right p-3">Members</th>
                  <th className="text-right p-3">Active Subs</th><th className="text-right p-3">Monthly Revenue</th>
                  <th className="text-right p-3">Access (MTD)</th>
                </tr></thead>
                <tbody>
                  {(data.sites || []).sort((a, b) => b.monthly_revenue - a.monthly_revenue).map(s => (
                    <tr key={s.site_id} className="border-b border-gray-800/50 hover:bg-gray-900/50">
                      <td className="p-3 text-white">{s.site_name} <span className="text-gray-600 text-xs">({s.site_code})</span></td>
                      <td className="p-3 text-right text-gray-300">{s.member_count}</td>
                      <td className="p-3 text-right text-gray-300">{s.active_subscriptions}</td>
                      <td className="p-3 text-right text-green-400 font-medium">£{s.monthly_revenue?.toFixed(2)}</td>
                      <td className="p-3 text-right text-gray-300">{s.access_this_month}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
